import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ScienceIcon from '@mui/icons-material/Science';
import BarChartIcon from '@mui/icons-material/BarChart';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TokenIcon from '@mui/icons-material/Token';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import {
  DEFAULT_AI_CONFIG,
  AVAILABLE_MINIMAX_MODELS,
  getAiConfig,
  fetchAiConfigFromDb,
  saveAiConfigToDb,
  calculateTokenCost,
  fetchAiQueryHistoryFromDb,
  getAiConsumptionMetrics,
  clearAiQueryHistory,
} from '../services/aiConfigService';
import { testMiniMaxConnection } from '../services/minimaxService';
import { auth } from '../firebase';

const AdminAiConfig = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState(getAiConfig());
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Estados de notificación
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Estados del Playground
  const [testPrompt, setTestPrompt] = useState('Hola, ¿cómo estás? Responde brevemente y explica quién eres.');
  const [testSystem, setTestSystem] = useState('Eres un asistente inteligente para la gestión de actas de proyectos.');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  // Estados de Métricas e Historial
  const [history, setHistory] = useState([]);
  const [metrics, setMetrics] = useState(getAiConsumptionMetrics());
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Cargar configuración desde Firestore al montar el componente
  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      try {
        const dbConfig = await fetchAiConfigFromDb();
        setConfig(dbConfig);
        const dbHistory = await fetchAiQueryHistoryFromDb(100);
        setHistory(dbHistory);
        setMetrics(getAiConsumptionMetrics(dbHistory));
      } catch (err) {
        console.error('Error al inicializar AdminAiConfig:', err);
      } finally {
        setInitialLoading(false);
      }
    };
    loadData();
  }, []);

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      const userEmail = auth.currentUser?.email || 'admin';
      const updated = await saveAiConfigToDb(config, userEmail);
      setConfig(updated);
      setSnackbar({
        open: true,
        message: '✅ Configuración global de IA guardada exitosamente en la base de datos.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error guardando configuración:', error);
      setSnackbar({
        open: true,
        message: '❌ Error al guardar en base de datos: ' + error.message,
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setConfig({ ...DEFAULT_AI_CONFIG });
    setSnackbar({
      open: true,
      message: 'Valores restablecidos a los recomendados por defecto (sin guardar aún).',
      severity: 'info',
    });
  };

  const handleRunTest = async () => {
    if (config.provider === 'minimax' && !config.minimaxApiKey) {
      setTestError('Debes ingresar y guardar una API Key de MiniMax para ejecutar la prueba.');
      return;
    }

    setTestRunning(true);
    setTestError(null);
    setTestResult(null);

    const startTime = performance.now();

    try {
      if (config.provider === 'minimax') {
        const res = await testMiniMaxConnection(testPrompt, config, testSystem);
        const durationMs = Math.round(performance.now() - startTime);
        const cost = calculateTokenCost(res.usage.inputTokens, res.usage.outputTokens, config);

        setTestResult({
          provider: 'MiniMax',
          model: config.minimaxModel,
          text: res.text,
          thinking: res.thinking,
          inputTokens: res.usage.inputTokens,
          outputTokens: res.usage.outputTokens,
          totalTokens: res.usage.totalTokens,
          costUsd: cost.formattedUsd,
          costCop: cost.formattedCop,
          durationMs,
        });
      } else {
        // Prueba de Gemini
        const { analyzeTranscription } = await import('../services/geminiService');
        const simulatedPrompt = `Por favor responde al siguiente mensaje: "${testPrompt}".`;
        const res = await analyzeTranscription(simulatedPrompt);
        const durationMs = Math.round(performance.now() - startTime);

        setTestResult({
          provider: 'Google Gemini',
          model: 'gemini-3.1-flash-lite',
          text: typeof res === 'object' ? JSON.stringify(res, null, 2) : String(res),
          thinking: null,
          inputTokens: Math.round(testPrompt.length / 4),
          outputTokens: 450,
          totalTokens: Math.round(testPrompt.length / 4) + 450,
          costUsd: '$0.00 (Incluido en cuota)',
          costCop: '$0 COP',
          durationMs,
        });
      }

      // Refrescar historial
      const updatedHistory = await fetchAiQueryHistoryFromDb(100);
      setHistory(updatedHistory);
      setMetrics(getAiConsumptionMetrics(updatedHistory));
    } catch (error) {
      console.error('Error en prueba de IA:', error);
      setTestError(error.message);
    } finally {
      setTestRunning(false);
    }
  };

  const handleRefreshHistory = async () => {
    setLoadingHistory(true);
    try {
      const dbHistory = await fetchAiQueryHistoryFromDb(100);
      setHistory(dbHistory);
      setMetrics(getAiConsumptionMetrics(dbHistory));
      setSnackbar({
        open: true,
        message: 'Historial de consultas sincronizado con la base de datos.',
        severity: 'info',
      });
    } catch (err) {
      console.error('Error al sincronizar historial:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('¿Estás seguro de que deseas limpiar el historial local de consultas de tokens?')) {
      clearAiQueryHistory();
      setHistory([]);
      setMetrics(getAiConsumptionMetrics([]));
      setSnackbar({
        open: true,
        message: 'Historial de consultas limpiado.',
        severity: 'info',
      });
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={40} sx={{ color: '#6164ff', mr: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Cargando configuración global de IA desde la base de datos...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: '0 auto' }}>
      {/* Encabezado */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8f9ff 0%, #eef1ff 100%)',
          border: '1px solid #e0e3ff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2.5,
              bgcolor: '#6164ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 4px 12px rgba(97, 100, 255, 0.3)',
            }}
          >
            <PsychologyIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e' }}>
              Configuración Global de Inteligencia Artificial
            </Typography>
            <Typography variant="body2" sx={{ color: '#5c6bc0' }}>
              Administra el motor de IA activo (Gemini o MiniMax), credenciales y monitorea el consumo de tokens y costos.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            icon={<SmartToyIcon />}
            label={
              config.provider === 'minimax'
                ? `Activo: MiniMax (${config.minimaxModel})`
                : 'Activo: Google Gemini'
            }
            color={config.provider === 'minimax' ? 'primary' : 'success'}
            sx={{ fontWeight: 700, px: 1, py: 2 }}
          />
        </Box>
      </Paper>

      {/* Pestañas */}
      <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: '#fafafa',
            '& .MuiTab-root': { py: 2, fontWeight: 700, fontSize: '0.95rem' },
          }}
        >
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Parámetros & Tarifas" />
          <Tab icon={<ScienceIcon />} iconPosition="start" label="Playground / Probador" />
          <Tab icon={<BarChartIcon />} iconPosition="start" label={`Consumo & Métricas (${history.length})`} />
        </Tabs>

        {/* ============================================================ */}
        {/* TAB 0: PARÁMETROS & TARIFAS */}
        {/* ============================================================ */}
        {activeTab === 0 && (
          <Box sx={{ p: 4 }}>
            {/* Card Selección de Proveedor */}
            <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, bgcolor: '#fbfbff' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>
                  1. Motor de Inteligencia Artificial para el Sistema
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Selecciona qué inteligencia artificial procesará las transcripciones de reuniones y archivos de asistencia de todos los usuarios.
                </Typography>

                <RadioGroup
                  row
                  value={config.provider}
                  onChange={(e) => handleConfigChange('provider', e.target.value)}
                >
                  <FormControlLabel
                    value="minimax"
                    control={<Radio color="primary" />}
                    label={
                      <Box sx={{ ml: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2d3748' }}>
                          🟢 MiniMax (MiniMax-M2.5 / M3)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Modelo avanzado de alta precisión con control de tokens y costos por documento.
                        </Typography>
                      </Box>
                    }
                    sx={{ mr: 4 }}
                  />
                  <FormControlLabel
                    value="gemini"
                    control={<Radio color="success" />}
                    label={
                      <Box sx={{ ml: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2d3748' }}>
                          🔵 Google Gemini
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Motor estándar configurado por defecto.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Parámetros de MiniMax */}
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>
              2. Parámetros del Modelo MiniMax
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="medium">
                  <InputLabel id="model-select-label">Modelo MiniMax</InputLabel>
                  <Select
                    labelId="model-select-label"
                    value={config.minimaxModel}
                    label="Modelo MiniMax"
                    onChange={(e) => handleConfigChange('minimaxModel', e.target.value)}
                  >
                    {AVAILABLE_MINIMAX_MODELS.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{m.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Key de MiniMax"
                  type={showApiKey ? 'text' : 'password'}
                  value={config.minimaxApiKey}
                  onChange={(e) => handleConfigChange('minimaxApiKey', e.target.value)}
                  placeholder="Ingresa tu API Key de MiniMax"
                  helperText="Se guarda de manera segura en Firestore y se comparte para el procesamiento global"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                          {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Base URL API"
                  value={config.minimaxBaseUrl}
                  onChange={(e) => handleConfigChange('minimaxBaseUrl', e.target.value)}
                  helperText="Usa '/api/minimax' (recomendado con proxy local) o 'https://api.minimax.io/anthropic'"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Tokens"
                  value={config.maxTokens}
                  onChange={(e) => handleConfigChange('maxTokens', e.target.value)}
                  helperText="Límite máximo de tokens de respuesta (recomendado: 4000)"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ step: '0.05', min: '0', max: '1' }}
                  label="Temperature"
                  value={config.temperature}
                  onChange={(e) => handleConfigChange('temperature', e.target.value)}
                  helperText="Creatividad del modelo (0.2 recomendado para precisión en actas)"
                />
              </Grid>
            </Grid>

            {/* Tarifas de Tokens */}
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>
              3. Tarifas de Tokens & Moneda
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Define los precios por millón de tokens para calcular con exactitud cuánto cuesta el procesamiento de cada acta en USD y COP.
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ step: '0.01' }}
                  label="Costo Entrada por 1M tokens (USD)"
                  value={config.inputPricePerMillion}
                  onChange={(e) => handleConfigChange('inputPricePerMillion', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText="Tarifa estándar MiniMax-M2.5: $0.15 USD / 1M"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ step: '0.01' }}
                  label="Costo Salida por 1M tokens (USD)"
                  value={config.outputPricePerMillion}
                  onChange={(e) => handleConfigChange('outputPricePerMillion', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText="Tarifa estándar MiniMax-M2.5: $1.10 USD / 1M"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tasa de Cambio (COP por 1 USD)"
                  value={config.copExchangeRate}
                  onChange={(e) => handleConfigChange('copExchangeRate', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">COP $</InputAdornment>,
                  }}
                  helperText="Tasa de conversión para mostrar costos en pesos colombianos"
                />
              </Grid>
            </Grid>

            {/* Acciones de guardado */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #eee' }}>
              <Button variant="outlined" color="inherit" onClick={handleResetDefaults} disabled={isSaving}>
                Restablecer Valores Iniciales
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{
                  bgcolor: '#6164ff',
                  px: 4,
                  py: 1.2,
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#5356e3' },
                }}
              >
                {isSaving ? 'Guardando en Base de Datos...' : 'Guardar en Base de Datos'}
              </Button>
            </Box>
          </Box>
        )}

        {/* ============================================================ */}
        {/* TAB 1: PLAYGROUND / PROBADOR RÁPIDO */}
        {/* ============================================================ */}
        {activeTab === 1 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>
              🧪 Probador Rápido en Vivo ({config.provider === 'minimax' ? `MiniMax ${config.minimaxModel}` : 'Google Gemini'})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Envía una solicitud de prueba al motor activo para verificar la conexión, la velocidad de respuesta, el razonamiento y el costo de tokens.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Prompt de Prueba"
                  multiline
                  rows={4}
                  value={testPrompt}
                  onChange={(e) => setTestPrompt(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="System Prompt"
                  multiline
                  rows={2}
                  value={testSystem}
                  onChange={(e) => setTestSystem(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleRunTest}
                  disabled={testRunning}
                  startIcon={testRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  sx={{
                    bgcolor: '#6164ff',
                    py: 1.5,
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#5356e3' },
                  }}
                >
                  {testRunning ? 'Ejecutando Consulta en Vivo...' : 'Ejecutar Consulta de Prueba'}
                </Button>

                {testError && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                    {testError}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {testResult ? (
                  <Box>
                    {/* Tarjetas de métricas del test */}
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f0f4ff', borderRadius: 2 }}>
                          <SpeedIcon color="primary" fontSize="small" />
                          <Typography variant="caption" display="block" color="text.secondary">Tiempo</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{testResult.durationMs} ms</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f0fdf4', borderRadius: 2 }}>
                          <TokenIcon color="success" fontSize="small" />
                          <Typography variant="caption" display="block" color="text.secondary">Tokens</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{testResult.totalTokens}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fffbeb', borderRadius: 2 }}>
                          <MonetizationOnIcon sx={{ color: '#d97706' }} fontSize="small" />
                          <Typography variant="caption" display="block" color="text.secondary">Costo USD</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{testResult.costUsd}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#faf5ff', borderRadius: 2 }}>
                          <MonetizationOnIcon color="secondary" fontSize="small" />
                          <Typography variant="caption" display="block" color="text.secondary">Costo COP</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{testResult.costCop}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Bloque Thinking (si existe) */}
                    {testResult.thinking && (
                      <Accordion sx={{ mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PsychologyIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                              Pensamiento / Razonamiento interno (Thinking Block)
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              color: '#475569',
                              maxHeight: 250,
                              overflowY: 'auto',
                            }}
                          >
                            {testResult.thinking}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Bloque de Respuesta */}
                    <Paper sx={{ p: 2.5, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          Respuesta generada ({testResult.provider}):
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                          fontSize: '0.9rem',
                          color: '#334155',
                          maxHeight: 320,
                          overflowY: 'auto',
                        }}
                      >
                        {testResult.text}
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 6,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #e2e8f0',
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      color: 'text.secondary',
                    }}
                  >
                    <ScienceIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Área de Resultados de Prueba
                    </Typography>
                    <Typography variant="caption">
                      Presiona "Ejecutar Consulta de Prueba" para visualizar la respuesta, tokens y costo.
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ============================================================ */}
        {/* TAB 2: MÉTRICAS & HISTORIAL DE CONSUMO */}
        {/* ============================================================ */}
        {activeTab === 2 && (
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  📊 Métricas Globales de Consumo de Tokens & Costos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Historial consolidado de análisis de documentos realizados por el sistema.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRefreshHistory}
                  disabled={loadingHistory}
                  startIcon={<RefreshIcon />}
                >
                  Sincronizar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleClearHistory}
                >
                  Limpiar Historial
                </Button>
              </Box>
            </Box>

            {/* Tarjetas Resumen */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#f0f4ff', border: '1px solid #dbe4ff' }}>
                  <Typography variant="caption" sx={{ color: '#4338ca', fontWeight: 700, textTransform: 'uppercase' }}>
                    Consultas Totales
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e1b4b', mt: 0.5 }}>
                    {metrics.totalQueries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {metrics.successfulQueriesCount} exitosas | {metrics.failedQueriesCount} fallidas
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700, textTransform: 'uppercase' }}>
                    Tokens Consumidos
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#052e16', mt: 0.5 }}>
                    {metrics.totalTokens.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    In: {metrics.totalInputTokens.toLocaleString()} | Out: {metrics.totalOutputTokens.toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700, textTransform: 'uppercase' }}>
                    Costo Total (USD)
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#451a03', mt: 0.5 }}>
                    ${metrics.totalCostUsd.toFixed(4)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Promedio: ${(metrics.avgCostPerQueryUsd || 0).toFixed(4)} / consulta
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#faf5ff', border: '1px solid #e9d5ff' }}>
                  <Typography variant="caption" sx={{ color: '#7e22ce', fontWeight: 700, textTransform: 'uppercase' }}>
                    Costo Total (COP)
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#3b0764', mt: 0.5 }}>
                    ${Math.round(metrics.totalCostCop).toLocaleString()} COP
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tasa: {config.copExchangeRate} COP / USD
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tabla de Historial */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
              Registro de Consultas de Documentos
            </Typography>

            {history.length === 0 ? (
              <Box sx={{ p: 5, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                <Typography variant="body2" color="text.secondary">
                  Aún no se han registrado consultas de documentos o pruebas de IA.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f8fafc' } }}>
                      <TableCell>Fecha / Hora</TableCell>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>Motor</TableCell>
                      <TableCell align="right">Tokens Entr.</TableCell>
                      <TableCell align="right">Tokens Sal.</TableCell>
                      <TableCell align="right">Costo USD</TableCell>
                      <TableCell align="right">Costo COP</TableCell>
                      <TableCell align="right">Tiempo</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {row.timestamp ? new Date(row.timestamp).toLocaleString('es-CO') : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{row.userEmail || 'usuario'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{row.queryType}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.fileName}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          <Chip size="small" label={row.model || row.provider} variant="outlined" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{(row.inputTokens || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{(row.outputTokens || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b45309' }}>
                          ${(row.costUsd || 0).toFixed(4)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#7e22ce' }}>
                          ${Math.round(row.costCop || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{row.durationMs || 0} ms</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.success ? 'OK' : 'Error'}
                            color={row.success ? 'success' : 'error'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Notificación Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3, borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAiConfig;
